import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Target, Plus, Search, Trash2, X, CheckCircle, AlertCircle, Edit2,
  Calendar, MessageSquare, Phone, Mail,
} from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isUserCoach } from '../../lib/auth';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import {
  getOrCreatePipeline, getStages, getLeads, createLead, updateLeadStage,
  updateLead, deleteLead, addStage, updateStage, deleteStage,
  type CrmPipeline, type CrmStage, type CrmLead,
} from '../../lib/crm';

type ModalMode = 'add-lead' | 'edit-lead' | 'add-stage' | 'edit-stage' | null;

const SOURCE_LABELS: Record<string, string> = {
  assessment_completion: 'Assessment',
  ai_coach_trial: 'AI Coach Trial',
  discover_page: 'Discover Page',
  manual: 'Manual',
};

const SOURCE_COLORS: Record<string, string> = {
  assessment_completion: 'bg-blue-100 text-blue-700',
  ai_coach_trial: 'bg-teal-100 text-teal-700',
  discover_page: 'bg-sage-100 text-sage-700',
  manual: 'bg-gray-100 text-gray-600',
};

function getSourceLabel(s: string) { return SOURCE_LABELS[s] ?? s; }
function getSourceColor(s: string) { return SOURCE_COLORS[s] ?? 'bg-gray-100 text-gray-600'; }

function isOverdue(date: string | null) {
  if (!date) return false;
  return new Date(date) < new Date(new Date().toDateString());
}

function isDueToday(date: string | null) {
  if (!date) return false;
  return date === new Date().toISOString().slice(0, 10);
}

export function SalesCRM() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [pipeline, setPipeline] = useState<CrmPipeline | null>(null);
  const [stages, setStages] = useState<CrmStage[]>([]);
  const [leads, setLeads] = useState<CrmLead[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [editingLead, setEditingLead] = useState<CrmLead | null>(null);
  const [editingStage, setEditingStage] = useState<CrmStage | null>(null);
  const [draggedLead, setDraggedLead] = useState<string | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedLead, setSelectedLead] = useState<CrmLead | null>(null);
  const [drawerSaving, setDrawerSaving] = useState(false);

  const [leadForm, setLeadForm] = useState({
    full_name: '', email: '', phone: '', source: 'manual', notes: '', stage_id: '',
    follow_up_date: '',
  });
  const [stageForm, setStageForm] = useState({ name: '', color: '#3b82f6' });

  // Drawer edit state
  const [drawerNotes, setDrawerNotes] = useState('');
  const [drawerFollowUp, setDrawerFollowUp] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }

    const init = async () => {
      try {
        const isCoach = await isUserCoach(user.id);
        if (!isCoach) { navigate('/login'); return; }

        const { data: coach } = await supabase
          .from('coaches')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!coach) { navigate('/login'); return; }
        setCoachId(coach.id);

        const p = await getOrCreatePipeline(coach.id);
        if (!p) { setError('Failed to load pipeline'); return; }
        setPipeline(p);

        const [s, l] = await Promise.all([getStages(p.id), getLeads(coach.id, p.id)]);
        setStages(s);
        setLeads(l);
      } catch (err) {
        console.error(err);
        setError('Failed to initialize CRM');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const reload = useCallback(async () => {
    if (!coachId || !pipeline) return;
    const [s, l] = await Promise.all([getStages(pipeline.id), getLeads(coachId, pipeline.id)]);
    setStages(s);
    setLeads(l);
    if (selectedLead) {
      const refreshed = l.find(x => x.id === selectedLead.id);
      if (refreshed) {
        setSelectedLead(refreshed);
        setDrawerNotes(refreshed.notes || '');
        setDrawerFollowUp(refreshed.follow_up_date || '');
      }
    }
  }, [coachId, pipeline, selectedLead]);

  const openDrawer = (lead: CrmLead) => {
    setSelectedLead(lead);
    setDrawerNotes(lead.notes || '');
    setDrawerFollowUp(lead.follow_up_date || '');
  };

  const closeDrawer = () => setSelectedLead(null);

  const saveDrawer = async () => {
    if (!selectedLead) return;
    setDrawerSaving(true);
    await updateLead(selectedLead.id, {
      notes: drawerNotes || null,
      follow_up_date: drawerFollowUp || null,
    });
    setDrawerSaving(false);
    await reload();
  };

  const handleDragStart = (leadId: string) => setDraggedLead(leadId);
  const handleDragEnd = () => { setDraggedLead(null); setDragOverStage(null); };

  const handleDrop = async (stageId: string) => {
    if (!draggedLead) return;
    const lead = leads.find(l => l.id === draggedLead);
    if (!lead || lead.stage_id === stageId) { handleDragEnd(); return; }
    setLeads(prev => prev.map(l => l.id === draggedLead ? { ...l, stage_id: stageId } : l));
    if (selectedLead?.id === draggedLead) setSelectedLead(prev => prev ? { ...prev, stage_id: stageId } : prev);
    handleDragEnd();
    await updateLeadStage(draggedLead, stageId);
  };

  const handleAddLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!coachId || !pipeline) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const { error: err } = await createLead({
        coach_id: coachId,
        pipeline_id: pipeline.id,
        stage_id: leadForm.stage_id || stages[0]?.id || '',
        full_name: leadForm.full_name,
        email: leadForm.email,
        phone: leadForm.phone || undefined,
        source: leadForm.source,
        notes: leadForm.notes || undefined,
        follow_up_date: leadForm.follow_up_date || undefined,
      });
      if (err) throw err;
      await reload();
      setModalMode(null);
      resetLeadForm();
    } catch (err: any) {
      setError(err.message || 'Failed to add lead');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLead) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const { error: err } = await updateLead(editingLead.id, {
        full_name: leadForm.full_name,
        email: leadForm.email,
        phone: leadForm.phone || null,
        notes: leadForm.notes || null,
        stage_id: leadForm.stage_id,
        follow_up_date: leadForm.follow_up_date || null,
      });
      if (err) throw err;
      await reload();
      setModalMode(null);
      resetLeadForm();
    } catch (err: any) {
      setError(err.message || 'Failed to update lead');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteLead = async (id: string) => {
    if (!confirm('Delete this lead?')) return;
    if (selectedLead?.id === id) closeDrawer();
    await deleteLead(id);
    await reload();
  };

  const handleAddStage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pipeline) return;
    setIsSubmitting(true);
    try {
      await addStage(pipeline.id, stageForm.name, stages.length, stageForm.color);
      await reload();
      setModalMode(null);
      setStageForm({ name: '', color: '#3b82f6' });
    } catch (err: any) {
      setError(err.message || 'Failed to add stage');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditStage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingStage) return;
    setIsSubmitting(true);
    try {
      await updateStage(editingStage.id, { name: stageForm.name, color: stageForm.color });
      await reload();
      setModalMode(null);
      setStageForm({ name: '', color: '#3b82f6' });
    } catch (err: any) {
      setError(err.message || 'Failed to update stage');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteStage = async (stageId: string) => {
    const stageLeads = leads.filter(l => l.stage_id === stageId);
    if (stageLeads.length > 0) {
      setError('Cannot delete a stage that has leads. Move or delete them first.');
      return;
    }
    if (!confirm('Delete this stage?')) return;
    await deleteStage(stageId);
    await reload();
  };

  const openEditLead = (lead: CrmLead) => {
    setEditingLead(lead);
    setLeadForm({
      full_name: lead.full_name,
      email: lead.email,
      phone: lead.phone || '',
      source: lead.source,
      notes: lead.notes || '',
      stage_id: lead.stage_id,
      follow_up_date: lead.follow_up_date || '',
    });
    setModalMode('edit-lead');
  };

  const openEditStage = (stage: CrmStage) => {
    setEditingStage(stage);
    setStageForm({ name: stage.name, color: stage.color });
    setModalMode('edit-stage');
  };

  const resetLeadForm = () => {
    setLeadForm({ full_name: '', email: '', phone: '', source: 'manual', notes: '', stage_id: '', follow_up_date: '' });
    setEditingLead(null);
  };

  const filteredLeads = leads.filter(l =>
    l.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const followUpLeads = leads.filter(l => l.follow_up_date && (isOverdue(l.follow_up_date) || isDueToday(l.follow_up_date)));

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Loading CRM..." />;
  }

  return (
    <div className="min-h-screen freud-bg flex flex-col">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-500 rounded-xl shadow-soft">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="freud-heading-xl text-chocolate-900">Sales Pipeline</h1>
                <p className="freud-text-sm text-chocolate-500">
                  {leads.length} leads across {stages.length} stages
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-chocolate-400" />
                <input
                  type="text"
                  placeholder="Search leads..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="freud-input pl-9 py-2 text-sm w-48"
                />
              </div>
              <button
                onClick={() => { setModalMode('add-stage'); setStageForm({ name: '', color: '#3b82f6' }); }}
                className="freud-btn-secondary flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" />
                Stage
              </button>
              <button
                onClick={() => { setModalMode('add-lead'); resetLeadForm(); }}
                className="freud-btn-primary flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add Lead
              </button>
            </div>
          </div>
        </div>
      </header>

      {error && (
        <div className="mx-4 mt-4 bg-coral-100 border border-coral-200 text-coral-700 px-4 py-3 rounded-xl flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Follow-up banner */}
      {followUpLeads.length > 0 && (
        <div className="mx-4 mt-4 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl flex items-center gap-3">
          <Calendar className="w-5 h-5 flex-shrink-0 text-amber-600" />
          <span className="text-sm font-medium">
            {followUpLeads.length} lead{followUpLeads.length !== 1 ? 's' : ''} need{followUpLeads.length === 1 ? 's' : ''} a follow-up today
            {followUpLeads.length <= 3 && ': ' + followUpLeads.map(l => l.full_name).join(', ')}
          </span>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* Kanban board */}
        <main className={`flex-1 overflow-x-auto p-4 transition-all duration-300 ${selectedLead ? 'mr-96' : ''}`}>
          <div className="flex gap-4 min-w-max h-full">
            {stages.map(stage => {
              const stageLeads = filteredLeads.filter(l => l.stage_id === stage.id);
              return (
                <div
                  key={stage.id}
                  className={`w-72 flex-shrink-0 flex flex-col rounded-xl border transition-colors ${
                    dragOverStage === stage.id ? 'border-blue-400 bg-blue-50/50' : 'border-cream-300 bg-white/60'
                  }`}
                  onDragOver={(e) => { e.preventDefault(); setDragOverStage(stage.id); }}
                  onDragLeave={() => setDragOverStage(null)}
                  onDrop={() => handleDrop(stage.id)}
                >
                  <div className="p-3 border-b border-cream-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }} />
                      <span className="text-sm font-semibold text-chocolate-800">{stage.name}</span>
                      <span className="text-xs text-chocolate-400 bg-cream-200 px-1.5 py-0.5 rounded-full">
                        {stageLeads.length}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEditStage(stage)} className="p-1 text-chocolate-400 hover:text-chocolate-600 rounded">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDeleteStage(stage.id)} className="p-1 text-chocolate-400 hover:text-coral-600 rounded">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-2 space-y-2 max-h-[calc(100vh-220px)]">
                    {stageLeads.map(lead => {
                      const overdue = isOverdue(lead.follow_up_date);
                      const dueToday = isDueToday(lead.follow_up_date);
                      const isSelected = selectedLead?.id === lead.id;
                      return (
                        <div
                          key={lead.id}
                          draggable
                          onDragStart={() => handleDragStart(lead.id)}
                          onDragEnd={handleDragEnd}
                          onClick={() => openDrawer(lead)}
                          className={`p-3 rounded-lg border shadow-sm cursor-pointer hover:shadow-md transition-all group ${
                            draggedLead === lead.id ? 'opacity-50' : ''
                          } ${isSelected ? 'border-blue-400 bg-blue-50/40' : 'bg-white border-cream-200'}`}
                        >
                          <div className="flex items-start justify-between mb-1">
                            <p className="text-sm font-medium text-chocolate-800 truncate flex-1">
                              {lead.full_name}
                            </p>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-1" onClick={e => e.stopPropagation()}>
                              <button onClick={() => openEditLead(lead)} className="p-0.5 text-chocolate-400 hover:text-chocolate-600">
                                <Edit2 className="w-3 h-3" />
                              </button>
                              <button onClick={() => handleDeleteLead(lead.id)} className="p-0.5 text-chocolate-400 hover:text-coral-600">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          <p className="text-xs text-chocolate-500 truncate">{lead.email}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${getSourceColor(lead.source)}`}>
                              {getSourceLabel(lead.source)}
                            </span>
                            <span className="text-xs text-chocolate-400">
                              {new Date(lead.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          {lead.follow_up_date && (
                            <div className={`mt-2 flex items-center gap-1 text-xs px-2 py-1 rounded-lg ${
                              overdue ? 'bg-red-50 text-red-600' : dueToday ? 'bg-amber-50 text-amber-700' : 'bg-cream-100 text-chocolate-500'
                            }`}>
                              <Calendar className="w-3 h-3" />
                              {overdue ? 'Overdue: ' : dueToday ? 'Today: ' : 'Follow up: '}
                              {new Date(lead.follow_up_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                            </div>
                          )}
                          {lead.first_message && (
                            <div className="mt-2 flex items-start gap-1.5 text-xs text-chocolate-400">
                              <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span className="line-clamp-2">{lead.first_message}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </main>

        {/* Lead Detail Drawer */}
        <AnimatePresence>
          {selectedLead && (
            <motion.aside
              key="drawer"
              initial={{ x: 400, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 400, opacity: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="fixed top-0 right-0 bottom-0 w-96 bg-white border-l border-cream-300 shadow-2xl flex flex-col z-30 overflow-hidden"
              style={{ top: '0' }}
            >
              {/* Drawer header */}
              <div className="px-5 py-4 border-b border-cream-200 flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-bold text-chocolate-900 truncate">{selectedLead.full_name}</h2>
                  <p className="text-sm text-chocolate-500 truncate">{selectedLead.email}</p>
                </div>
                <button onClick={closeDrawer} className="ml-3 p-1.5 text-chocolate-400 hover:text-chocolate-700 rounded-lg hover:bg-cream-100 flex-shrink-0">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-5">
                {/* Meta */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-cream-50 rounded-xl p-3">
                    <p className="text-xs text-chocolate-400 mb-1">Source</p>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getSourceColor(selectedLead.source)}`}>
                      {getSourceLabel(selectedLead.source)}
                    </span>
                  </div>
                  <div className="bg-cream-50 rounded-xl p-3">
                    <p className="text-xs text-chocolate-400 mb-1">Stage</p>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: stages.find(s => s.id === selectedLead.stage_id)?.color || '#ccc' }} />
                      <span className="text-xs font-medium text-chocolate-700 truncate">
                        {stages.find(s => s.id === selectedLead.stage_id)?.name || 'Unknown'}
                      </span>
                    </div>
                  </div>
                  <div className="bg-cream-50 rounded-xl p-3">
                    <p className="text-xs text-chocolate-400 mb-1">Added</p>
                    <p className="text-xs font-medium text-chocolate-700">
                      {new Date(selectedLead.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                  {selectedLead.phone && (
                    <div className="bg-cream-50 rounded-xl p-3">
                      <p className="text-xs text-chocolate-400 mb-1">Phone</p>
                      <p className="text-xs font-medium text-chocolate-700 truncate">{selectedLead.phone}</p>
                    </div>
                  )}
                </div>

                {/* Quick actions */}
                <div>
                  <p className="text-xs font-semibold text-chocolate-500 uppercase tracking-wide mb-2">Quick Actions</p>
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={`mailto:${selectedLead.email}`}
                      className="flex items-center gap-1.5 text-xs px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors font-medium"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      Send Email
                    </a>
                    {selectedLead.phone && (
                      <a
                        href={`tel:${selectedLead.phone}`}
                        className="flex items-center gap-1.5 text-xs px-3 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors font-medium"
                      >
                        <Phone className="w-3.5 h-3.5" />
                        Call
                      </a>
                    )}
                  </div>
                </div>

                {/* First message from AI conversation */}
                {selectedLead.first_message && (
                  <div>
                    <p className="text-xs font-semibold text-chocolate-500 uppercase tracking-wide mb-2">First Message</p>
                    <div className="bg-teal-50 border border-teal-100 rounded-xl p-4">
                      <div className="flex items-start gap-2">
                        <MessageSquare className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-chocolate-700 leading-relaxed">{selectedLead.first_message}</p>
                      </div>
                      <p className="text-xs text-teal-600 mt-2">From AI conversation page</p>
                    </div>
                  </div>
                )}

                {/* Follow-up date */}
                <div>
                  <p className="text-xs font-semibold text-chocolate-500 uppercase tracking-wide mb-2">Follow-up Date</p>
                  <input
                    type="date"
                    value={drawerFollowUp}
                    onChange={e => setDrawerFollowUp(e.target.value)}
                    className="freud-input text-sm"
                  />
                  {drawerFollowUp && isOverdue(drawerFollowUp) && (
                    <p className="text-xs text-red-600 mt-1">This follow-up is overdue</p>
                  )}
                  {drawerFollowUp && isDueToday(drawerFollowUp) && (
                    <p className="text-xs text-amber-600 mt-1">Due today</p>
                  )}
                </div>

                {/* Move stage */}
                <div>
                  <p className="text-xs font-semibold text-chocolate-500 uppercase tracking-wide mb-2">Move to Stage</p>
                  <div className="flex flex-wrap gap-2">
                    {stages.map(stage => (
                      <button
                        key={stage.id}
                        onClick={async () => {
                          setLeads(prev => prev.map(l => l.id === selectedLead.id ? { ...l, stage_id: stage.id } : l));
                          setSelectedLead(prev => prev ? { ...prev, stage_id: stage.id } : prev);
                          await updateLeadStage(selectedLead.id, stage.id);
                        }}
                        className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-all ${
                          selectedLead.stage_id === stage.id
                            ? 'border-transparent text-white font-semibold shadow-sm'
                            : 'border-cream-300 text-chocolate-600 hover:border-chocolate-300 bg-white'
                        }`}
                        style={selectedLead.stage_id === stage.id ? { backgroundColor: stage.color } : {}}
                      >
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }} />
                        {stage.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <p className="text-xs font-semibold text-chocolate-500 uppercase tracking-wide mb-2">Notes</p>
                  <textarea
                    value={drawerNotes}
                    onChange={e => setDrawerNotes(e.target.value)}
                    rows={5}
                    placeholder="Add notes about this lead, what you discussed, next steps..."
                    className="freud-input text-sm resize-none"
                  />
                </div>
              </div>

              {/* Drawer footer */}
              <div className="px-5 py-4 border-t border-cream-200 flex items-center gap-3">
                <button
                  onClick={saveDrawer}
                  disabled={drawerSaving}
                  className="freud-btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {drawerSaving ? (
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  {drawerSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => handleDeleteLead(selectedLead.id)}
                  className="p-2.5 text-coral-500 hover:text-coral-700 hover:bg-coral-50 rounded-xl transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </motion.aside>
          )}
        </AnimatePresence>
      </div>

      {/* Lead Modal */}
      <AnimatePresence>
        {(modalMode === 'add-lead' || modalMode === 'edit-lead') && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="freud-card-elevated p-6 w-full max-w-md m-4 max-h-[90vh] overflow-y-auto"
            >
              <h2 className="text-xl font-bold text-chocolate-900 mb-6">
                {modalMode === 'edit-lead' ? 'Edit Lead' : 'Add New Lead'}
              </h2>
              <form onSubmit={modalMode === 'edit-lead' ? handleEditLead : handleAddLead} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Full Name</label>
                  <input type="text" value={leadForm.full_name} onChange={(e) => setLeadForm({ ...leadForm, full_name: e.target.value })} required className="freud-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Email</label>
                  <input type="email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} required className="freud-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Phone</label>
                  <input type="text" value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} className="freud-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Stage</label>
                  <select value={leadForm.stage_id} onChange={(e) => setLeadForm({ ...leadForm, stage_id: e.target.value })} className="freud-input">
                    {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Source</label>
                  <select value={leadForm.source} onChange={(e) => setLeadForm({ ...leadForm, source: e.target.value })} className="freud-input">
                    <option value="manual">Manual</option>
                    <option value="assessment_completion">Assessment Completion</option>
                    <option value="ai_coach_trial">AI Coach Trial</option>
                    <option value="discover_page">Discover Page</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Follow-up Date</label>
                  <input type="date" value={leadForm.follow_up_date} onChange={(e) => setLeadForm({ ...leadForm, follow_up_date: e.target.value })} className="freud-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Notes</label>
                  <textarea value={leadForm.notes} onChange={(e) => setLeadForm({ ...leadForm, notes: e.target.value })} rows={3} className="freud-input resize-none" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => { setModalMode(null); resetLeadForm(); }} className="freud-btn-secondary flex-1" disabled={isSubmitting}>Cancel</button>
                  <button type="submit" className="freud-btn-primary flex-1" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : (modalMode === 'edit-lead' ? 'Save' : 'Add Lead')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Stage Modal */}
      <AnimatePresence>
        {(modalMode === 'add-stage' || modalMode === 'edit-stage') && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="freud-card-elevated p-6 w-full max-w-md m-4"
            >
              <h2 className="text-xl font-bold text-chocolate-900 mb-6">
                {modalMode === 'edit-stage' ? 'Edit Stage' : 'Add Stage'}
              </h2>
              <form onSubmit={modalMode === 'edit-stage' ? handleEditStage : handleAddStage} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Stage Name</label>
                  <input type="text" value={stageForm.name} onChange={(e) => setStageForm({ ...stageForm, name: e.target.value })} required className="freud-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Color</label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={stageForm.color} onChange={(e) => setStageForm({ ...stageForm, color: e.target.value })} className="w-10 h-10 rounded-lg border border-cream-300 cursor-pointer" />
                    <input type="text" value={stageForm.color} onChange={(e) => setStageForm({ ...stageForm, color: e.target.value })} className="freud-input flex-1" />
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setModalMode(null)} className="freud-btn-secondary flex-1" disabled={isSubmitting}>Cancel</button>
                  <button type="submit" className="freud-btn-primary flex-1" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : (modalMode === 'edit-stage' ? 'Save' : 'Add Stage')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
